import * as React from 'react';

import { ZonedDateTime } from '@internationalized/date';
import {
  Badge,
  Button,
  ConfirmPopoverButton,
  Content,
  DataGrid,
  DataGridColumn,
  EmptyProps,
  EntityDetails,
  isDateWithinHours,
  Loading,
  LocalizedDate,
  LocalizedTime,
  Row,
  Toolbar,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  LucideMinusCircle,
  LucidePlusCircle,
  LucideTrash2,
} from 'lucide-react';
import { useLocation } from 'wouter';

import {
  counterpartyGroupDeleteAtom,
  counterpartyGroupRemoveAccountsAtom,
} from '../../_graphql/mutationCounterpartyGroupCrud';
import * as state from '../../_state/counterpartyGroupDetail.state';
import { useTranslations } from '../../i18n/useTranslations';
import { pricingRoutePaths } from '../../routes';
import { OPERATION_TYPE_COLORS } from '../../types/pricing';

import { AddAccountsDialog } from './AddAccountsDialog';
import { CounterpartyGroupFormDialog } from './CounterpartyGroupFormDialog';

type MemberRow = {
  id: string;
  accountId: string;
  accountName: string;
  accountType: string;
  addedAt: ZonedDateTime | null;
  addedByName: string | null;
};

type CounterpartyGroupTab = 'members';

interface MembersTabContentProps {
  onAddMembers: () => void;
  isLoading: boolean;
  columns: DataGridColumn<MemberRow>[];
  columnVisibilityState: Record<string, boolean>;
  dataSource: MemberRow[];
  ariaLabel: string;
  addButtonLabel: string;
  memberCountLabel: string;
  emptyPanelProps: EmptyProps;
}

//
// ⚛️ ⚛️ ⚛️ MEMBERS TAB CONTENT ⚛️ ⚛️ ⚛️
//
const MembersTabContent = ({
  onAddMembers,
  isLoading,
  columns,
  columnVisibilityState,
  dataSource,
  ariaLabel,
  addButtonLabel,
  memberCountLabel,
  emptyPanelProps,
}: MembersTabContentProps) => (
  <div className="@MembersTabContent flex h-full flex-col">
    <Toolbar className="flex justify-between">
      <div className="text-sm text-neutral/60">{memberCountLabel}</div>
      <div className="ml-auto flex gap-2">
        <Button
          variant="solid"
          color="primary"
          icon={<LucidePlusCircle />}
          onPress={onAddMembers}
        >
          {addButtonLabel}
        </Button>
      </div>
    </Toolbar>
    <Content className="h-full">
      <DataGrid<MemberRow>
        isLoading={isLoading}
        columns={columns}
        columnVisibilityState={columnVisibilityState}
        dataSource={dataSource}
        rowKey="id"
        aria-label={ariaLabel}
        className="w-full text-sm"
        fullWidth
        emptyPanelProps={emptyPanelProps}
      />
    </Content>
  </div>
);

//
// ⚛️ ⚛️ ⚛️ COUNTERPARTY GROUP DETAIL VIEW ⚛️ ⚛️ ⚛️
//
export const CounterpartyGroupDetailView = () => {
  // Translations
  const t = useTranslations();

  // Navigation
  const [, navigate] = useLocation();

  // States
  const [currentTab, setCurrentTab] =
    React.useState<CounterpartyGroupTab>('members');
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = React.useState(false);
  const [removingAccountId, setRemovingAccountId] = React.useState<
    string | null
  >(null);

  // Atoms
  const _counterpartyGroupId = useAtomValue(state.$counterpartyGroupId);
  const {
    data: groupData,
    isFetching: groupFetching,
    refetch: refetchGroup,
  } = useAtomValue(state.$remoteCounterpartyGroup);
  const {
    data: membersData,
    isFetching: membersFetching,
    refetch: refetchMembers,
  } = useAtomValue(state.$remoteGroupMembers);
  const { data: memberAccounts } = useAtomValue(state.$remoteMemberAccounts);
  const { data: memberDetails, refetch: refetchMemberDetails } = useAtomValue(
    state.$remoteMemberDetails,
  );
  const isLoading = useAtomValue(state.$isCounterpartyGroupLoading);
  const error = useAtomValue(state.$counterpartyGroupError);
  const removeAccounts = useSetAtom(counterpartyGroupRemoveAccountsAtom({}));
  const deleteGroup = useSetAtom(counterpartyGroupDeleteAtom({}));

  // Data hooks
  const toast = useToast();

  const counterpartyGroup = groupData?.counterpartyGroups?.[0];
  const memberAccountIds: string[] = (membersData as string[]) ?? [];

  // Memoized values
  const accountMap = React.useMemo(() => {
    const map = new Map<string, { name: string; type: string }>();
    memberAccounts?.forEach((account) =>
      map.set(account.id, { name: account.name, type: account.type }),
    );
    return map;
  }, [memberAccounts]);

  const memberDetailMap = React.useMemo(() => {
    const map = new Map<
      string,
      { addedAt: ZonedDateTime; addedByName: string | null }
    >();
    memberDetails?.forEach((detail) =>
      map.set(detail.accountId, {
        addedAt: detail.timestamps.createdAt,
        addedByName: detail.addedByName,
      }),
    );
    return map;
  }, [memberDetails]);

  const memberRows: MemberRow[] = React.useMemo(
    () =>
      memberAccountIds.map((accountId) => {
        const account = accountMap.get(accountId);
        const detail = memberDetailMap.get(accountId);
        return {
          id: accountId,
          accountId,
          accountName: account?.name ?? accountId,
          accountType: account?.type ?? '',
          addedAt: detail?.addedAt ?? null,
          addedByName: detail?.addedByName ?? null,
        };
      }),
    [memberAccountIds, accountMap, memberDetailMap],
  );

  const isUpdatedAtRecent = React.useMemo(
    () => isDateWithinHours(counterpartyGroup?.timestamps.updatedAt, 12),
    [counterpartyGroup],
  );

  // Callbacks
  const handleBack = React.useCallback(() => {
    navigate(pricingRoutePaths.counterpartyGroups);
  }, [navigate]);

  const handleRemoveAccount = React.useCallback(
    async (accountId: string) => {
      if (!_counterpartyGroupId) return;
      try {
        setRemovingAccountId(accountId);
        await removeAccounts({
          groupId: _counterpartyGroupId,
          accountIds: [accountId],
        });
        toast.success(t.pricing.counterpartyGroups.toast.accountsRemoved);
        refetchMembers?.();
        refetchMemberDetails?.();
      } catch (err) {
        toast.error(
          t.pricing.counterpartyGroups.toast.deleteError(
            err instanceof Error ? err.message : 'Unknown error',
          ),
        );
      } finally {
        setRemovingAccountId(null);
      }
    },
    [
      _counterpartyGroupId,
      removeAccounts,
      toast,
      t,
      refetchMembers,
      refetchMemberDetails,
    ],
  );

  const handleDeleteGroup = React.useCallback(async () => {
    if (!_counterpartyGroupId) return;
    try {
      await deleteGroup({ id: _counterpartyGroupId });
      toast.success(t.pricing.counterpartyGroups.toast.deleted);
      navigate(pricingRoutePaths.counterpartyGroups);
    } catch (err) {
      toast.error(
        t.pricing.counterpartyGroups.toast.deleteError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    }
  }, [_counterpartyGroupId, deleteGroup, toast, t, navigate]);

  const handleEditSuccess = React.useCallback(() => {
    refetchGroup?.();
  }, [refetchGroup]);

  const memberColumns: DataGridColumn<MemberRow>[] = React.useMemo(
    () => [
      {
        key: 'accountName',
        header: t.pricing.counterpartyGroups.detail.members.accountNameHeader,
        width: 300,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left text-sm">
            {row.accountName}
          </div>
        ),
      },
      {
        key: 'accountType',
        header: t.pricing.counterpartyGroups.detail.members.accountTypeHeader,
        width: 120,
        cell: ({ row }) =>
          row.accountType ? (
            <Badge color="neutral" size="small">
              {row.accountType}
            </Badge>
          ) : null,
      },
      {
        key: 'addedBy',
        header: t.pricing.counterpartyGroups.detail.members.addedByHeader,
        width: 160,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left text-sm text-neutral/60">
            {row.addedByName ?? '—'}
          </div>
        ),
      },
      {
        key: 'addedAt',
        header: t.pricing.counterpartyGroups.detail.members.addedDateHeader,
        width: 160,
        cell: ({ row }) => (
          <div className="flex min-h-8 items-center text-left text-sm text-neutral/60">
            {row.addedAt ? <LocalizedDate date={row.addedAt} /> : '—'}
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        width: 100,
        cell: ({ row }) => (
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              color="failure"
              icon={<LucideMinusCircle size={14} />}
              onPress={() => handleRemoveAccount(row.accountId)}
              isDisabled={removingAccountId === row.accountId}
            >
              {t.pricing.counterpartyGroups.detail.members.removeButton}
            </Button>
          </div>
        ),
      },
    ],
    [handleRemoveAccount, removingAccountId, t],
  );

  const memberColumnVisibility = React.useMemo(
    () => Object.fromEntries(memberColumns.map((col) => [col.key, true])),
    [memberColumns],
  );

  const handleOpenAddDialog = React.useCallback(() => {
    setIsAddDialogOpen(true);
  }, []);

  const handleCloseAddDialog = React.useCallback(() => {
    setIsAddDialogOpen(false);
  }, []);

  const emptyMembersProps = React.useMemo(
    () => ({
      title: t.pricing.counterpartyGroups.detail.members.empty.title,
      description: (
        <span>
          {t.pricing.counterpartyGroups.detail.members.empty.description}
        </span>
      ),
      action: (
        <Button
          variant="solid"
          color="primary"
          icon={<LucidePlusCircle />}
          onPress={handleOpenAddDialog}
        >
          {t.pricing.counterpartyGroups.detail.members.addButton}
        </Button>
      ),
      centered: true,
    }),
    [t, handleOpenAddDialog],
  );

  const badges = React.useMemo(() => {
    if (!counterpartyGroup) return null;
    return (
      <div className="flex gap-2">
        <Badge
          color={OPERATION_TYPE_COLORS[counterpartyGroup.operationType]}
          size="small"
        >
          {counterpartyGroup.operationType}
        </Badge>
      </div>
    );
  }, [counterpartyGroup]);

  const headerActions = React.useMemo(
    () => (
      <div className="flex gap-2">
        <Button
          variant="outline"
          color="neutral"
          onPress={() => setIsEditDialogOpen(true)}
        >
          {t.pricing.counterpartyGroups.edit.title}
        </Button>
        <ConfirmPopoverButton
          onConfirm={handleDeleteGroup}
          message={
            counterpartyGroup
              ? t.pricing.counterpartyGroups.deleteConfirm.message(
                  counterpartyGroup.name,
                )
              : ''
          }
          confirmLabel={t.common.actions.delete}
          cancelLabel={t.common.actions.cancel}
          renderTrigger={(props) => (
            <Button
              {...props}
              variant="outline"
              color="failure"
              icon={<LucideTrash2 size={14} />}
            />
          )}
        />
      </div>
    ),
    [t, handleDeleteGroup, counterpartyGroup],
  );

  const membersTabContent = React.useCallback(
    () => (
      <MembersTabContent
        onAddMembers={handleOpenAddDialog}
        isLoading={membersFetching}
        columns={memberColumns}
        columnVisibilityState={memberColumnVisibility}
        dataSource={memberRows}
        ariaLabel={t.pricing.counterpartyGroups.detail.tabs.members}
        addButtonLabel={t.pricing.counterpartyGroups.detail.members.addButton}
        memberCountLabel={t.pricing.counterpartyGroups.detail.memberCount(
          memberRows.length,
        )}
        emptyPanelProps={emptyMembersProps}
      />
    ),
    [
      t,
      memberColumns,
      memberColumnVisibility,
      memberRows,
      membersFetching,
      emptyMembersProps,
      handleOpenAddDialog,
    ],
  );

  if (isLoading || groupFetching) {
    return (
      <div className="@CounterpartyGroupDetailView flex h-full flex-col">
        <div className="p-6">
          <Loading />
        </div>
      </div>
    );
  }

  if (error || !counterpartyGroup) {
    return (
      <div className="@CounterpartyGroupDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          {t.common.errors.notFound(t.pricing.counterpartyGroups.detail.title)}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          {t.pricing.counterpartyGroups.detail.backButton}
        </Button>
      </div>
    );
  }

  return (
    <div className="@CounterpartyGroupDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as CounterpartyGroupTab)}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={counterpartyGroup.name}
        subtitle={
          <Row gap1>
            {t.pricing.counterpartyGroups.detail.subtitle.created}{' '}
            <LocalizedDate date={counterpartyGroup.timestamps.createdAt} />
            {counterpartyGroup.updatedByUser && (
              <>
                &bull;{' '}
                {t.pricing.counterpartyGroups.detail.subtitle.lastUpdatedBy}{' '}
                {counterpartyGroup.updatedByUser.name}{' '}
                {t.pricing.counterpartyGroups.detail.subtitle.on}{' '}
                <LocalizedDate
                  date={counterpartyGroup.timestamps.updatedAt}
                  dateStyle="short"
                />
                {isUpdatedAtRecent && (
                  <LocalizedTime
                    time={counterpartyGroup.timestamps.updatedAt}
                    timeStyle="short"
                  />
                )}
              </>
            )}
          </Row>
        }
        badges={badges}
        headerActions={headerActions}
        tabs={{
          members: {
            title: t.pricing.counterpartyGroups.detail.tabs.members,
            content: membersTabContent,
          },
        }}
      />
      {_counterpartyGroupId && (
        <AddAccountsDialog
          isOpen={isAddDialogOpen}
          onClose={handleCloseAddDialog}
          groupId={_counterpartyGroupId}
          existingMemberIds={memberAccountIds}
        />
      )}
      {counterpartyGroup && (
        <CounterpartyGroupFormDialog
          isOpen={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          onSuccess={handleEditSuccess}
          group={counterpartyGroup}
        />
      )}
    </div>
  );
};
